import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  Card,
  CardContent,
  LinearProgress
} from '@mui/material';
import {
  CheckCircle,
  Clock,
  Warning,
  FileText,
  Building,
  Shield
} from '@phosphor-icons/react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import BusinessDetailsForm from './BusinessDetailsForm';
import DocumentUpload from './DocumentUpload';
import { BusinessContext } from '../../context/BusinessContext';

const steps = [
  'Business Details',
  'Upload Documents',
  'Review & Submit',
  'Verification Status'
];

// Business type mapping for display
const businessTypes = [
  { value: 'sole_trader', label: 'Sole Trader' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'limited_company', label: 'Limited Company' },
  { value: 'llc', label: 'Limited Liability Company (LLC)' },
  { value: 'other', label: 'Other' }
];

// Helper function to get business type label
const getBusinessTypeLabel = (value) => {
  const type = businessTypes.find(t => t.value === value);
  return type ? type.label : value;
};

function BusinessVerification() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const businessContext = useContext(BusinessContext);
  const selectedBusiness = businessContext?.selectedBusiness;
  const selectBusiness = businessContext?.selectBusiness;
  const refreshBusinesses = businessContext?.refreshBusinesses;
  const [activeStep, setActiveStep] = useState(0);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Keep context in sync with URL: URL is source of truth so "Add another business" -> verification/NEW_ID
  // saves to the correct business instead of redirecting to the previously selected one
  useEffect(() => {
    if (!businessId || businessId === 'undefined' || businessId === 'null') return;
    const selectedId = selectedBusiness?.business?._id?.toString();
    if (selectedId !== businessId && selectBusiness) {
      selectBusiness(businessId);
    }
  }, [businessId, selectedBusiness?.business?._id, selectBusiness]);

  // Helper function to check if any documents have been uploaded
  const hasUploadedDocuments = (verificationDocuments) => {
    if (!verificationDocuments) return false;
    
    // Check if any document has actual content (not just an empty object)
    return Object.keys(verificationDocuments).some(key => {
      if (key === 'additionalDocuments') return false; // Skip additionalDocuments array
      const doc = verificationDocuments[key];
      return doc && doc.url && doc.filename && doc.uploadedAt;
    });
  };

  useEffect(() => {
    fetchBusinessStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [businessId]);

  // Ensure verified businesses always show step 3
  useEffect(() => {
    if (business?.verification) {
      const verification = business.verification;
      if (verification.isVerified === true || verification.verificationStatus === 'approved') {
        if (activeStep !== 3) {
          setActiveStep(3);
        }
      }
    }
  }, [business, activeStep]);

  const fetchBusinessStatus = async () => {
    try {
      setLoading(true);

      // Validate businessId - use selectedBusiness if businessId from URL is invalid
      let validBusinessId = businessId;
      if (!validBusinessId || validBusinessId === 'undefined' || validBusinessId === 'null') {
        if (selectedBusiness?.business?._id) {
          validBusinessId = selectedBusiness.business._id.toString();
          // Redirect to correct URL
          navigate(`/supplier/verification/${validBusinessId}`, { replace: true });
        } else {
          setError('Business ID is required. Please select a business first.');
          setLoading(false);
          return;
        }
      }

      const response = await api.get(`/api/business-verification/${validBusinessId}/status`);
      setBusiness(response.data.business);

      // Determine current step based on verification status
      const verification = response.data.business.verification;

      // Safety check for verification object
      if (!verification) {
        setActiveStep(0);
        return;
      }

      // If business is already verified, show status page
      if (verification.isVerified === true || verification.verificationStatus === 'approved') {
        setActiveStep(3);
      } else if (verification.verificationStatus === 'rejected') {
        setActiveStep(3);
      } else if (verification.verificationStatus === 'under_review') {
        setActiveStep(3);
      } else {
        if (response.data.business.businessDetails && 
            response.data.business.businessDetails.businessType) {
          setActiveStep(1); // Start at document upload if business details are complete
        } else {
          setActiveStep(0); // Start at business details if not complete
        }
      }
    } catch (error) {
      setError('Failed to load business verification status');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Helper function to determine if back navigation should be allowed
  const canGoBack = () => {
    // Can't go back from step 0 (first step)
    if (activeStep === 0) return false;
    
    // Can't go back from step 3 (verification status) if already submitted
    if (activeStep === 3) {
      const verification = business?.verification;
      return !verification || 
             verification.verificationStatus === 'pending' ||
             verification.verificationStatus === 'rejected';
    }
    
    return true;
  };

  const handleBusinessDetailsSubmit = async (businessDetails) => {
    try {
      setSubmitting(true);
      await api.post(`/api/business-verification/${businessId}/submit`, {
        businessDetails
      });
      
      // Refresh business data - this will automatically set the correct step
      await fetchBusinessStatus();
      
      // Manually advance to next step if business details are complete
      setActiveStep(1);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to submit business details');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDocumentsUpload = async () => {
    await fetchBusinessStatus();
  };

  const handleFinalSubmission = async () => {
    try {
      setSubmitting(true);
      await api.post(`/api/business-verification/${businessId}/submit-for-review`);
      
      // Refresh business data - this will automatically set the correct step
      await fetchBusinessStatus();
      // Update BusinessContext so VerificationBanner and layout reflect new status without page refresh
      if (refreshBusinesses) await refreshBusinesses();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to submit for review');
    } finally {
      setSubmitting(false);
    }
  };

  const _getVerificationStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle size={24} color="#4caf50" weight="fill" />;
      case 'rejected':
        return <Warning size={24} color="#f44336" weight="fill" />;
      case 'under_review':
        return <Clock size={24} color="#ff9800" weight="fill" />;
      default:
        return <Clock size={24} color="#9e9e9e" weight="fill" />;
    }
  };

  const _getVerificationStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'under_review':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
          Loading verification status...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/supplier/dashboard')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <Shield size={32} weight="regular" color="#1976d2" />
        <Typography variant="h4" component="h1" sx={{ ml: 2, fontWeight: 'bold' }}>
          Business Verification
        </Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" mb={4}>
        Complete your business verification to start receiving bookings and appear in supplier search results.
      </Typography>


      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                onClick={() => {
                  // Allow clicking on steps only if they can be navigated to
                  if (index < activeStep && canGoBack()) {
                    setActiveStep(index);
                  }
                }}
                sx={{
                  cursor: index < activeStep && canGoBack() ? 'pointer' : 'default',
                  '&:hover': index < activeStep && canGoBack() ? {
                    '& .MuiStepLabel-label': {
                      color: 'primary.main'
                    }
                  } : {}
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step Content */}
      <Paper sx={{ p: 4 }}>
        {/* Prevent showing forms if business is already verified */}
        {activeStep === 0 && business && !business.verification?.isVerified && business.verification?.verificationStatus !== 'approved' && (
          <BusinessDetailsForm
            businessDetails={business.businessDetails}
            onSubmit={handleBusinessDetailsSubmit}
            loading={submitting}
            onBack={canGoBack() ? handleBack : null}
          />
        )}

        {activeStep === 1 && business && !business.verification?.isVerified && business.verification?.verificationStatus !== 'approved' && (
          <DocumentUpload
            businessId={businessId}
            businessType={business.businessDetails?.businessType}
            verificationDocuments={business.verificationDocuments}
            onUploadComplete={handleDocumentsUpload}
            onBack={canGoBack() ? handleBack : null}
            onNext={handleNext}
          />
        )}

        {activeStep === 2 && business && !business.verification?.isVerified && business.verification?.verificationStatus !== 'approved' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Submit
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              Please review your business details and uploaded documents before submitting for verification.
            </Typography>

            {/* Business Details Summary */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Building size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  Business Details
                </Typography>
                {business.businessDetails ? (
                  <Box>
                    <Typography><strong>Business Type:</strong> {getBusinessTypeLabel(business.businessDetails.businessType)}</Typography>
                    {business.businessDetails.businessNumber && (
                      <Typography><strong>Business Number:</strong> {business.businessDetails.businessNumber}</Typography>
                    )}
                    {business.businessDetails.vatNumber && (
                      <Typography><strong>VAT Number:</strong> {business.businessDetails.vatNumber}</Typography>
                    )}
                    {business.businessDetails.tradingName && (
                      <Typography><strong>Trading Name:</strong> {business.businessDetails.tradingName}</Typography>
                    )}
                  </Box>
                ) : (
                  <Typography color="text.secondary">No business details submitted</Typography>
                )}
              </CardContent>
            </Card>

            {/* Documents Summary */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <FileText size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  Uploaded Documents
                </Typography>
                {hasUploadedDocuments(business.verificationDocuments) ? (
                  <Box>
                    {Object.entries(business.verificationDocuments).map(([key, doc]) => {
                      // Only show documents that have actual content
                      if (key === 'additionalDocuments' || !doc || !doc.url || !doc.filename || !doc.uploadedAt) {
                        return null;
                      }
                      return (
                        <Box key={key} display="flex" alignItems="center" mb={1}>
                          <CheckCircle size={16} color="#4caf50" style={{ marginRight: 8 }} />
                          <Typography variant="body2">
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography color="text.secondary">No documents uploaded</Typography>
                )}
              </CardContent>
            </Card>

            <Box display="flex" justifyContent="space-between">
              {canGoBack() && (
                <Button onClick={handleBack} size="large" sx={{ minWidth: 120 }}>
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleFinalSubmission}
                disabled={!business.businessDetails || !hasUploadedDocuments(business.verificationDocuments) || submitting}
                size="large"
                sx={{ minWidth: 150, ml: canGoBack() ? 'auto' : 0 }}
              >
                {submitting ? 'Submitting...' : 'Submit for Review'}
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 3 && business && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Verification Status
            </Typography>

            {(business.verification.isVerified || business.verification.verificationStatus === 'approved') && (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  ✅ Your business is verified
                </Typography>
                <Typography>
                  Your business is verified and will appear in supplier search results. 
                  You can start receiving bookings from couples.
                </Typography>
              </Alert>
            )}

            {business.verification.verificationStatus === 'rejected' && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Verification Rejected
                </Typography>
                <Typography>
                  {business.verification.rejectionReason || 'Your verification has been rejected. Please review the requirements and resubmit.'}
                </Typography>
              </Alert>
            )}

            {business.verification.verificationStatus === 'under_review' && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="h6" component="div">
                    Under Review
                  </Typography>
                  <Typography component="div">
                    Your verification documents are being reviewed by our team.
                    This process typically takes 1-3 business days. You'll be notified once the review is complete.
                  </Typography>
                </Box>
              </Alert>
            )}

            {business.verification.verificationStatus === 'pending' && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Verification Pending
                </Typography>
                <Typography>
                  Please complete the verification process to make your business visible to couples.
                </Typography>
              </Alert>
            )}

            <Box display="flex" justifyContent="space-between">
              {canGoBack() && (
                <Button onClick={handleBack} size="large" sx={{ minWidth: 120 }}>
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => navigate('/supplier/dashboard')}
                size="large"
                sx={{ minWidth: 150, ml: canGoBack() ? 'auto' : 0 }}
              >
                Back to Dashboard
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default BusinessVerification;
